import React, { useState } from 'react'
import IconButton from '@material-ui/core/IconButton'
import Avatar from '@material-ui/core/Avatar'
import ExpandMoreOutlinedIcon from '@material-ui/icons/ExpandMoreOutlined'
import ListItemText from '@material-ui/core/ListItemText'
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction'
import MenuItem from '@material-ui/core/MenuItem'
import MenuList from '@material-ui/core/MenuList'
import Divider from '@material-ui/core/Divider'
import Popover from '@material-ui/core/Popover'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import OpenInNewOutlinedIcon from '@material-ui/icons/OpenInNewOutlined'
import { makeStyles } from '@material-ui/core/styles'
import { Link as RouterLink } from 'react-router-dom'
import { useAuth } from 'lib/auth'
import * as maybe from 'lib/maybe'
import AccountRouterLink from 'components/account-router-link'
import { show } from 'lib/support'
import SensitiveInfoWrapper from 'lib/auth/SensitiveInfoWrapper'
import { STRIPE_CUSTOMER_PORTAL_URL } from 'env'

const useStyles = makeStyles(theme => ({
  icon: {
    color: theme.palette.text.secondary,
    minWidth: theme.spacing(4),
  },
  menuItemText: {
    color: theme.palette.text.primary,
  },
}))

export default function AccountDropdown() {
  const { user, isLoggedIn, isCoach, isConsumer } = useAuth()
  const classes = useStyles()
  const hasPaidTierSubscription = user?.coach?.hasPaidTierSubscription
  const isActiveMember =
    user?.coach?.pgaMembership?.membershipStatus === 'Active'

  const [anchorEl, setAnchorEl] = useState(null)

  const toggleAccountMenu = event => {
    setAnchorEl(el => (el ? null : event.currentTarget))
  }

  const closeMenu = () => {
    setAnchorEl(null)
  }

  const avatarContent = maybe
    .of(user)
    .map(({ name, email }) => name || email)
    .map(name => name.charAt(0).toUpperCase())
    .withDefault('U')

  if (!isLoggedIn) return null

  return (
    <>
      <IconButton
        aria-label="account of current user"
        aria-controls="account-dropdown-menu"
        aria-haspopup="true"
        onClick={toggleAccountMenu}
      >
        <Avatar
          style={{
            width: '40px',
            height: '40px',
            fontSize: '0.75em',
          }}
          src={user?.coach?.coachProfile?.profilePhoto}
        >
          {avatarContent}
        </Avatar>
        <ExpandMoreOutlinedIcon fontSize="small" />
      </IconButton>

      <Popover
        id="account-dropdown-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={closeMenu}
      >
        <MenuList autoFocusItem>
          {isCoach ? (
            <>
              <Box display="flex" alignItems="center" pl={2} py={1}>
                <Avatar
                  style={{
                    width: '48px',
                    height: '48px',
                    fontSize: '0.75em',
                  }}
                  src={user?.coach?.coachProfile?.profilePhoto}
                >
                  {avatarContent}
                </Avatar>
                <Box px={2}>
                  <Typography variant="body1">{user.coach?.name}</Typography>
                  <Typography variant="caption">
                    {user.coach?.coachProfile?.title}
                  </Typography>
                </Box>
              </Box>
              <AccountDropdownItem
                component={AccountRouterLink}
                path="/account"
                text="Account Details"
                onClick={closeMenu}
              />
              <SensitiveInfoWrapper>
                <AccountDropdownItem
                  component={AccountRouterLink}
                  path="/account/payments"
                  text="Payment Accounts"
                  onClick={closeMenu}
                />
              </SensitiveInfoWrapper>
              {hasPaidTierSubscription && (
                <SensitiveInfoWrapper>
                  <MenuItem
                    component={'a'}
                    target="_blank"
                    href={STRIPE_CUSTOMER_PORTAL_URL}
                    onClick={closeMenu}
                  >
                    <ListItemText classes={{ primary: classes.menuItemText }}>
                      Manage Plan
                    </ListItemText>
                    <ListItemSecondaryAction>
                      <OpenInNewOutlinedIcon />
                    </ListItemSecondaryAction>
                  </MenuItem>
                </SensitiveInfoWrapper>
              )}
              {isActiveMember && (
                <AccountDropdownItem
                  component={AccountRouterLink}
                  path="/account/sponsorships"
                  text="PGA Coach Sponsorships"
                  onClick={closeMenu}
                />
              )}
              {(user.familyCupSignedUp || user.jrlSignedUp) && (
                <AccountDropdownItem
                  component={AccountRouterLink}
                  path="/account/requirements"
                  text="Requirements"
                  onClick={closeMenu}
                />
              )}
              {user.isAcademyOwner && (
                <>
                  <Divider />
                  <AccountDropdownItem
                    component={AccountRouterLink}
                    path="/account/academy-coaches"
                    text="Manage Coaching Center Coaches"
                    onClick={closeMenu}
                  />
                  <AccountDropdownItem
                    component={AccountRouterLink}
                    path="/account/bay-reservations"
                    text="Manage Bay Rentals"
                    onClick={closeMenu}
                  />
                  <AccountDropdownItem
                    component={AccountRouterLink}
                    path="/account/coaching-center"
                    text="Manage Coaching Center Students"
                    onClick={closeMenu}
                  />
                </>
              )}
              {(window.location.href.includes('pga-coach') ||
                window.location.href.includes('junior-league')) && (
                <>
                  <Divider />
                  <MenuItem
                    onClick={() => {
                      show()
                      closeMenu()
                    }}
                  >
                    <ListItemText classes={{ primary: classes.menuItemText }}>
                      Contact Us
                    </ListItemText>
                  </MenuItem>
                  <MenuItem
                    component={'a'}
                    target="_blank"
                    href="https://support.pga.com/en/collections/2683103-pga-members"
                    onClick={closeMenu}
                  >
                    <ListItemText classes={{ primary: classes.menuItemText }}>
                      PGA Coach Help Center
                    </ListItemText>
                    <ListItemSecondaryAction>
                      <OpenInNewOutlinedIcon />
                    </ListItemSecondaryAction>
                  </MenuItem>
                </>
              )}
            </>
          ) : null}
          {isConsumer ? (
            <>
              <AccountDropdownItem
                component={AccountRouterLink}
                path="/account/payments/transactions"
                text="Transactions"
                onClick={closeMenu}
              />
              <AccountDropdownItem
                component={AccountRouterLink}
                path="/account/students"
                text="Accounts"
                onClick={closeMenu}
              />
            </>
          ) : null}
          <Divider />
          <MenuItem component={RouterLink} to="/logout" onClick={closeMenu}>
            <ListItemText classes={{ primary: classes.menuItemText }}>
              Logout
            </ListItemText>
          </MenuItem>
        </MenuList>
      </Popover>
    </>
  )
}

function AccountDropdownItem({ component, path, text, onClick }) {
  const classes = useStyles()
  return (
    <MenuItem component={component} to={path} onClick={onClick}>
      <ListItemText classes={{ primary: classes.menuItemText }}>
        {text}
      </ListItemText>
    </MenuItem>
  )
}
